/**
 * Take secrets out of the address bar before anything can read them, and hand
 * them to the view in memory instead.
 *
 * Password-reset links are `/reset-password?token=<token>`, and a token sitting
 * in `window.location` is a secret in every surface that reads it:
 *
 *   - Mixpanel's automatic page views record `$current_url` verbatim, query
 *     string included — 28 distinct reset tokens across 58 events reached the
 *     analytics project this way (NAT-2172 defect 1);
 *   - rrweb writes the same href into the Session Replay Meta event, where the
 *     masking flags do not reach — which is why NAT-2172 item 4 gates NAT-2171;
 *   - the browser keeps the full URL in history and sends it as a `Referer`.
 *
 * WHY THIS RUNS AT MODULE SCOPE, not on mount. NAT-2172 item 4 suggests
 * `history.replaceState` "as soon as the reset view mounts". That is too late
 * for the only case that exists: the customer arrives on this URL by clicking
 * the link in the email, so the app bootstraps with the token already in the
 * address bar. `analytics.ts` initialises during that bootstrap — page view
 * tracked, recording possibly started — before React has rendered anything.
 * So `analytics.ts` calls this above its own `mixpanel.init`, and the reset view
 * reads the value from here rather than from the query string.
 *
 * Note this narrows exposure rather than eliminating it: the token still
 * travelled to the browser in a URL, and anything that read `location` before
 * this ran (a browser extension, the referrer of an already-loaded subresource)
 * saw it. Short-lived tokens remain the actual control — NAT-2172 item 3.
 */

/** Query parameters that carry a secret and must never survive in the URL. */
export const SECRET_QUERY_PARAMS = ['token'] as const;

export type SecretQueryParam = (typeof SECRET_QUERY_PARAMS)[number];

const captured = new Map<SecretQueryParam, string>();

let hasRun = false;

/**
 * Move every secret parameter out of the URL and into module memory, rewriting
 * the address bar with `replaceState` so the token is not left in history
 * either. Idempotent: the first call wins, later ones are no-ops.
 */
export const captureAndStripUrlSecrets = (): void => {
  if (hasRun) return;
  // Guarded on the shape rather than on `typeof window`: unit tests stub a
  // minimal window, and a throw here would take down the whole app bootstrap.
  if (typeof window === 'undefined') return;
  if (typeof window.location?.href !== 'string') return;
  hasRun = true;

  const url = new URL(window.location.href);
  let found = false;

  SECRET_QUERY_PARAMS.forEach((name) => {
    const value = url.searchParams.get(name);
    if (value === null) return;
    captured.set(name, value);
    url.searchParams.delete(name);
    found = true;
  });

  if (!found) return;

  // Preserve the existing history state: React Router keys its location off it,
  // and this runs before the router is created.
  window.history?.replaceState?.(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`,
  );
};

/**
 * The value that was in the URL when the app loaded, for the view that needs it.
 * Captures on first read too, so a caller that runs before `analytics.ts` has
 * been imported still gets the token rather than an empty address bar.
 */
export const getCapturedSecret = (name: SecretQueryParam): string | null => {
  captureAndStripUrlSecrets();
  return captured.get(name) ?? null;
};
